import React, { useEffect } from 'react'
import { Chart, Bar, Line } from 'react-chartjs-2'
import moment from 'moment'
import numeral from 'numeral'
import { Box } from '@chakra-ui/react'
import { useTranslation } from 'react-i18next'
import {
  CategoryScale,
  LinearScale,
  BarElement,
  Title,
  Tooltip,
  Legend,
} from 'chart.js'
import type {
  BandwidthAndRequest,
  GraphData,
  GraphOptions,
  AnalyticsAPIResponse,
  BandwidthGraphData,
  VideoProcessingUnitDateWiseUsage,
  ExtensionUnitDateWiseUsage,
} from '../../../interfaces'
import { EXTENSION_DISPLAY_NAMES } from '../../../../utils/extensions'
import { StorageDateWiseUsage } from '../../../interfaces/Analytics'

Chart.register(CategoryScale, LinearScale, BarElement, Title, Tooltip, Legend)

const COLORS = [
  '#FB8C00', // 1
  '#80DEEA', // 2
  '#5E35B1', // 3
  '#4CAF50', // 4
  '#FF8A80', // 5
  '#B0BEC5', // 6
  '#795548', // 7
  '#BF360C', // 8
  '#C6FF00', // 9
  '#006064', // 10
  '#DCEDC8', // 11
  '#B2EBF2', // 12
  '#5C6BC0', // 13
  '#6A1B9A', // 14
  '#B71C1C', // 15
  '#448AFF', // 16
  '#64FFDA', // 17
  '#AFB42B', // 18
  '#FFD54F', // 19
  '#FF6E40', // 20
]

const getGraphData = (
  data: BandwidthAndRequest,
  labelText: string,
): GraphData => {
  try {
    if (
      !(
        data.dateWise &&
        data.dateWise.labels.length > 0 &&
        data.dateWise.datasets.length > 0 &&
        data.dateWise.datasets[0].data.length > 0
      )
    ) {
      // do nothing
    }
  } catch (e) {
    // set empty object
    data.dateWise = {
      labels: [],
      datasets: [
        {
          data: [],
        },
      ],
    }
  }

  const result = {
    labels: data.dateWise?.labels.slice(0, data.dateWise.labels.length),
    datasets: [
      {
        label: labelText,
        data: data.dateWise?.datasets[0].data.slice(
          0,
          data.dateWise.datasets[0].data.length,
        ),
        borderColor: 'rgb(168, 197, 221)',
        backgroundColor: 'rgba(168, 197, 221, 0.6)',
        fill: true,
      },
    ],
  }
  return result
}

interface ContextObject {
  dataset: {
    label: string
    data: number[]
  }
  dataIndex: number
}

const getGraphOptions = (
  type: 'bandwidth' | 'request',
  labelData: string[],
): GraphOptions => {
  const graphOptions = {
    animation: {
      duration: 0,
    },
    responsive: true,
    maintainAspectRatio: true,
    tension: 0.5,
    scales: {
      y: {
        ticks: {
          suggestedMin: 0,
          callback(value) {
            if (type === 'bandwidth') {
              return numeral(value).format('0.0 ib').replace('i', '')
            }
            return numeral(value).format('0.0 a')
          },
        },
      },
      x: {
        ticks: {
          callback(value: number) {
            // value is index in case of x-axis as mentioned in docs
            return moment(labelData[value], 'YYYY-MM-DD').format('D MMM')
          },
        },
      },
    },
    plugins: {
      tooltip: {
        callbacks: {
          label(context: ContextObject) {
            let label = context.dataset.label || ''

            if (type === 'bandwidth') {
              label = numeral(context.dataset.data[context.dataIndex])
                .format('0.0 ib')
                .replace('i', '')
            } else {
              label = numeral(context.dataset.data[context.dataIndex]).format(
                '0.0 a',
              )
            }
            return label
          },
        },
      },
    },
  }
  return graphOptions
}

interface Props {
  type: 'bandwidth' | 'request'
  data: AnalyticsAPIResponse
  bandwidthGraphData: BandwidthGraphData | null
  setBandwidthGraphData: Function
}

const Graph = ({
  type,
  data,
  bandwidthGraphData,
  setBandwidthGraphData,
}: Props): React.ReactElement => {
  const { t } = useTranslation()

  const bandwidthData = data.bandwidth
  const requestData = data.request

  useEffect(() => {
    if (bandwidthData) {
      const graphData = getGraphData(
        bandwidthData,
        'Bandwidth Usage with ImageKit',
      )
      if (graphData.labels) {
        setBandwidthGraphData({
          data: graphData,
          options: getGraphOptions('bandwidth', graphData.labels),
        })
      }
    }
  }, [bandwidthData])

  if (!bandwidthGraphData) {
    return <Box>{t('no_data_available', 'No Data Available')}</Box>
  }

  if (type === 'bandwidth') {
    return (
      <div className="chart-container">
        <Line
          data={bandwidthGraphData.data}
          options={bandwidthGraphData.options}
        />
      </div>
    )
  } else if (requestData) {
    const graphData = getGraphData(requestData, 'Number of Requests')
    return (
      <div className="chart-container">
        {graphData.labels && (
          <Line
            data={graphData}
            options={getGraphOptions('request', graphData.labels)}
          />
        )}
      </div>
    )
  }
  return <Box>{t('no_data_available', 'No Data Available')}</Box>
}

interface VPUGraphProps {
  vpuUsageData?: VideoProcessingUnitDateWiseUsage[]
  dateRange?: {
    startDate: Date
    endDate: Date
  }
}

interface StorageGraphProp {
  storageData?: StorageDateWiseUsage[]
  dateRange?: {
    startDate: Date
    endDate: Date
  }
}

interface ExtensionUnitsGraphProps {
  extensionUsageData?: ExtensionUnitDateWiseUsage[]
  dateRange?: {
    startDate: Date
    endDate: Date
  }
}

export const VPUGraph = ({
  vpuUsageData,
  dateRange,
}: VPUGraphProps): React.ReactElement => {
  const { t } = useTranslation()

  if (!vpuUsageData || !vpuUsageData.length || !dateRange) {
    return <Box>{t('no_data_available', 'No Data Available')}</Box>
  }

  const dateRangeDiff = moment(dateRange.endDate).diff(
    moment(dateRange.startDate),
    'days',
  )

  if (dateRangeDiff <= 0) {
    return <Box>{t('no_data_available', 'No Data Available')}</Box>
  }

  const LABEL_KEY_FORMAT = 'YYYYMMDD'

  const datewiseKeys = Array.from({ length: dateRangeDiff + 1 }).map((_, i) =>
    moment(dateRange.startDate).add(i, 'day').format(LABEL_KEY_FORMAT),
  )

  const datewiseMap = new Map<string, null | VideoProcessingUnitDateWiseUsage>(
    datewiseKeys.map(key => [key, null]),
  )

  vpuUsageData.forEach(dayUsage => {
    const key = moment(dayUsage.date).utc(false).format(LABEL_KEY_FORMAT)
    datewiseMap.set(key, dayUsage)
  })

  const vpuUsageToDisplay: VideoProcessingUnitDateWiseUsage[] =
    datewiseKeys.map(
      timeKey =>
        datewiseMap.get(timeKey) ?? {
          date: moment(timeKey, LABEL_KEY_FORMAT).toISOString(),
          duration: {},
          vpuUsage: {
            SD: 0,
            HD: 0,
            '4K': 0,
            '8K': 0,
            '16K': 0,
          },
        },
    )

  return (
    <Bar
      options={{
        scales: {
          x: { stacked: true },
          y: { stacked: true },
        },
        interaction: {
          mode: 'index' as const,
          intersect: false,
        },
      }}
      data={{
        labels: vpuUsageToDisplay.map(({ date }) =>
          moment(date).utc(false).format('Do MMM'),
        ),
        datasets: [
          {
            label: 'SD',
            data: vpuUsageToDisplay.map(({ vpuUsage }) => vpuUsage.SD),
            backgroundColor: COLORS[0],
          },
          {
            label: 'HD',
            data: vpuUsageToDisplay.map(({ vpuUsage }) => vpuUsage.HD),
            backgroundColor: COLORS[1],
          },
          {
            label: '4K',
            data: vpuUsageToDisplay.map(({ vpuUsage }) => vpuUsage['4K']),
            backgroundColor: COLORS[2],
          },
          {
            label: '8K',
            data: vpuUsageToDisplay.map(({ vpuUsage }) => vpuUsage['8K']),
            backgroundColor: COLORS[3],
          },
          {
            label: '16K',
            data: vpuUsageToDisplay.map(({ vpuUsage }) => vpuUsage['16K']),
            backgroundColor: COLORS[4],
          },
        ],
      }}
    />
  )
}

export const ExtensionUnitsGraph = ({
  extensionUsageData,
  dateRange,
}: ExtensionUnitsGraphProps): React.ReactElement => {
  const { t } = useTranslation()

  if (!extensionUsageData || !extensionUsageData.length || !dateRange) {
    return <Box>{t('no_data_available', 'No Data Available')}</Box>
  }

  const dateRangeDiff = moment(dateRange.endDate).diff(
    moment(dateRange.startDate),
    'days',
  )

  if (dateRangeDiff <= 0) {
    return <Box>{t('no_data_available', 'No Data Available')}</Box>
  }

  const LABEL_KEY_FORMAT = 'YYYYMMDD'

  const datewiseKeys = Array.from({ length: dateRangeDiff + 1}).map((_, i) =>
    moment(dateRange.startDate).add(i, 'day').format(LABEL_KEY_FORMAT),
  )

  const datewiseMap = new Map<string, null | ExtensionUnitDateWiseUsage>(
    datewiseKeys.map(key => [key, null]),
  )

  extensionUsageData.forEach(dayUsage => {
    const key = moment(dayUsage.date).utc(false).format(LABEL_KEY_FORMAT)
    datewiseMap.set(key, dayUsage)
  })

  return (
      <Bar
        options={{
          scales: {
            x: { stacked: true },
            y: { stacked: true },
          },
          interaction: {
            mode: 'index' as const,
            intersect: false,
          },
          plugins: {
            tooltip: {
              callbacks: {
                label: function (context) {
                  const extensionName = context.dataset.label;
                  // @ts-ignore
                  const unitConsumed = context.dataset.unitConsumed[context.dataIndex];
                  // @ts-ignore
                  const operationCount = context.dataset.operationCounts[context.dataIndex];
                  if(unitConsumed > 0) return `${extensionName}: ${operationCount} operations, ${unitConsumed} units`;
                  return "";
                }
              }
            }
          }
        }}
        data={{
          labels: extensionUsageData.map(({ date }) =>
            moment(new Date(date)).utc(false).format('Do MMM'),
          ),
          datasets: Object.keys(EXTENSION_DISPLAY_NAMES).map((extensionName, index) => ({
            label: EXTENSION_DISPLAY_NAMES[extensionName],
            data: extensionUsageData.map(
              item => item.operationCount[extensionName] ?? 0,
            ),
            unitConsumed: extensionUsageData.map(item => item.unitConsumed[extensionName] ?? 0),
            operationCounts: extensionUsageData.map(item => item.operationCount[extensionName] ?? 0),
            backgroundColor: COLORS[index]
          })),
        }}
      />
    )
}

export const StorageGraph = ({
  storageData,
  dateRange,
}: StorageGraphProp): React.ReactElement => {
  const { t } = useTranslation()

  if (!storageData || !storageData.length || !dateRange) {
    return <Box>{t('no_data_available', 'No Data Available')}</Box>
  }

  const dateRangeDiff = moment(dateRange.endDate).diff(
    moment(dateRange.startDate),
    'days',
  )

  if (dateRangeDiff <= 0) {
    return <Box>{t('no_data_available', 'No Data Available')}</Box>
  }

  const LABEL_KEY_FORMAT = 'YYYYMMDD'

  const datewiseKeys = Array.from({ length: dateRangeDiff + 1 }).map((_, i) =>
    moment(dateRange.startDate).add(i, 'day').format(LABEL_KEY_FORMAT),
  )

  const datewiseMap = new Map<string, null | StorageDateWiseUsage>(
    datewiseKeys.map(key => [key, null]),
  )

  storageData.forEach(dayUsage => {
    const key = moment(dayUsage.date).utc(false).format(LABEL_KEY_FORMAT)
    datewiseMap.set(key, dayUsage)
  })

  const usageToDisplay: StorageDateWiseUsage[] =
    datewiseKeys.map(
      timeKey =>
        datewiseMap.get(timeKey) ?? {
          date: moment(timeKey, LABEL_KEY_FORMAT).toISOString(),
          avgUploadStorage: 0,
          avgOriginalCacheStorage: 0
        },
    )

  return (
    <Bar
      options={{
        scales: {
          x: { stacked: true },
          y: { 
            stacked: true,
            ticks: {
              callback: function(value) {
                return numeral(value).format('0.0 ib').replace('i', '')
              }
            }
          },
        },
        interaction: {
          mode: 'index',
          intersect: false,
        },
        plugins: {
          tooltip: {
            callbacks: {
              label: function(tooltipItem) {
                return numeral(tooltipItem.raw).format('0.0 ib').replace('i', '')
              }
            }
          }
        }
      }}
      data={{
        labels: usageToDisplay.map(({ date }) =>
          moment(date).utc(false).format('Do MMM'),
        ),
        datasets: [
          {
            label: 'Media Library Storage',
            data: usageToDisplay.map(({ avgUploadStorage }) => avgUploadStorage),
            backgroundColor: COLORS[0],
          },
          {
            label: 'Original Cache Storage',
            data: usageToDisplay.map(({ avgOriginalCacheStorage }) => avgOriginalCacheStorage),
            backgroundColor: COLORS[1],
          },
        ],
      }}
    />
  )
}

export default Graph
